import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import FeatureCard from 'tio-reimagined/components/feature-card';
import { eq } from 'tio-ui/utilities';
import SectionHeader from 'tio-reimagined/components/section-header';
import SmartActionsSection from 'tio-employee/components/reimagined/smart-actions-section';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import NavCard from 'tio-reimagined/components/nav-card';
import PageHero from 'tio-reimagined/components/page-hero';
import GlassCard from 'tio-reimagined/components/glass-card';
import CtaBanner from 'tio-reimagined/components/cta-banner';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  getCurrentDemoClientData,
  getCurrentDemoScenario,
} from 'tio-employee/utils/career-pathways-demo-data';
import {
  PhShieldCheck,
  PhArrowRight,
  PhCurrencyDollar,
  PhCalculator,
  PhLightbulb,
  PhCalendar,
  PhScales,
  PhClockCountdown,
  PhRobot,
} from 'ember-phosphor-icons';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';

interface IdrIndexModel {
  loans: LoanAccount[];
  hasLinkedLoans: boolean;
  isDemoMode: boolean;
  dataSourceInfo: DataSourceInfo;
}

interface IdrIndexSignature {
  Args: {
    model: IdrIndexModel;
  };
}

// IDR Plan types for display
interface IdrPlan {
  name: string;
  abbreviation: string;
  monthlyPayment: string;
  totalPaid: string;
  forgiveness: string;
  timeline: string;
  bestFor: string;
  recommended: boolean;
}

const DEMO_IDR_PLANS: IdrPlan[] = [
  {
    name: 'SAVE Plan',
    abbreviation: 'SAVE',
    monthlyPayment: '$142',
    totalPaid: '$34,080',
    forgiveness: '$28,170',
    timeline: '20 years',
    bestFor: 'Lowest monthly payment with forgiveness',
    recommended: true,
  },
  {
    name: 'PAYE',
    abbreviation: 'PAYE',
    monthlyPayment: '$185',
    totalPaid: '$44,400',
    forgiveness: '$17,850',
    timeline: '20 years',
    bestFor: 'Balance of payment and forgiveness',
    recommended: false,
  },
  {
    name: 'IBR (New)',
    abbreviation: 'IBR',
    monthlyPayment: '$195',
    totalPaid: '$46,800',
    forgiveness: '$15,450',
    timeline: '20 years',
    bestFor: 'Good option if SAVE unavailable',
    recommended: false,
  },
  {
    name: 'ICR',
    abbreviation: 'ICR',
    monthlyPayment: '$265',
    totalPaid: '$63,600',
    forgiveness: '$0',
    timeline: '25 years',
    bestFor: 'Parent PLUS consolidation only',
    recommended: false,
  },
];

export default class IdrIndex extends Component<IdrIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get demoScenario() {
    return getCurrentDemoScenario();
  }

  get loansSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.hasLinkedLoans ? 'live' : 'placeholder';
  }

  get totalBalance() {
    if (this.isDemoMode || !this.args.model.hasLinkedLoans) {
      return '$45,230';
    }
    const total = this.args.model.loans.reduce((sum, l) => sum + l.balance, 0);
    return `$${total.toLocaleString()}`;
  }

  get monthlyPayment() {
    if (this.isDemoMode || !this.args.model.hasLinkedLoans) {
      return '$387';
    }
    const total = this.args.model.loans.reduce((sum, l) => sum + l.monthlyPayment, 0);
    return `$${total.toLocaleString()}`;
  }

  get idrPlans() {
    return DEMO_IDR_PLANS;
  }

  get smartActions(): SmartAction[] {
    return [
      {
        id: 'compare-plans',
        title: 'Compare IDR Plans',
        description: 'See which income-driven plan saves you the most money over time.',
        icon: PhScales,
        priority: 'urgent',
        category: 'money',
        cta: 'Compare Plans',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'recertify',
        title: 'Recertify Your Income',
        description: 'Annual recertification keeps your payments affordable. Stay on track.',
        icon: PhCalendar,
        priority: 'high',
        category: 'deadline',
        cta: 'Recertify Now',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'forgiveness-estimator',
        title: 'Estimate Your Forgiveness',
        description: 'Calculate how much you could have forgiven under your IDR plan.',
        icon: PhCalculator,
        priority: 'medium',
        category: 'money',
        cta: 'Estimate Savings',
        route: 'authenticated.reimagined.advisor',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <PageHero @gradient="cool" @decoration="circles" @rounded="2xl">
        <:default>
          <div class="tio-row mb-4">
            <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
              <PhShieldCheck class="h-7 w-7 text-white" />
            </div>
            <div>
              <Heading @level={{2}} class="text-white font-bold">
                Income-Driven Repayment
              </Heading>
              <Text class="text-teal-100 text-sm">
                Pay what you can afford, not what they demand
              </Text>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <DataIndicator @source={{this.loansSource}}>
              <GlassCard @label="Current Balance" @value={{this.totalBalance}} />
            </DataIndicator>
            <GlassCard @label="Monthly Payment" @value={{this.monthlyPayment}} />
            <GlassCard
              @label="Potential Savings"
              @value="$28,170"
              @accent="default"
              @subtext="with SAVE plan forgiveness"
            />
          </div>
        </:default>
      </PageHero>

      {{! Smart Actions }}
      <SmartActionsSection
        @actions={{this.smartActions}}
        @title="Recommended Actions"
        @subtitle="Steps to optimize your repayment"
      />

      {{! IDR Plan Comparison }}
      <SectionHeader
        @icon={{PhScales}}
        @title="IDR Plan Comparison"
        @subtitle="Based on your income and loan balance"
        @iconClass="tio-fg-info"
      />
      <div class="space-y-4">
        {{#each this.idrPlans as |plan|}}
          <div
            class="relative bg-white rounded-xl border
              {{if plan.recommended 'border-teal-500 ring-2 ring-teal-200' 'tio-border-default'}}
              p-6 transition-all hover:shadow-md"
          >
            {{#if plan.recommended}}
              <div
                class="absolute -top-3 left-6 px-3 py-1 tio-bg-info text-white text-xs font-semibold rounded-full"
              >
                Recommended
              </div>
            {{/if}}
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div class="flex-1">
                <div class="tio-row">
                  <Heading @level={{3}}>
                    {{plan.name}}
                  </Heading>
                  <Chip
                    @intent={{if plan.recommended "primary" "default"}}
                  >{{plan.abbreviation}}</Chip>
                </div>
                <Text class="tio-fg-muted text-sm mt-1">
                  {{plan.bestFor}}
                </Text>
              </div>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <Text class="tio-fg-muted text-xs uppercase">Monthly</Text>
                  <div class="text-lg font-bold tio-fg-primary">
                    {{plan.monthlyPayment}}
                  </div>
                </div>
                <div>
                  <Text class="tio-fg-muted text-xs uppercase">Total Paid</Text>
                  <div class="text-lg font-bold tio-fg-primary">
                    {{plan.totalPaid}}
                  </div>
                </div>
                <div>
                  <Text class="tio-fg-muted text-xs uppercase">Forgiven</Text>
                  <div
                    class="text-lg font-bold
                      {{if (eq plan.forgiveness '$0') 'tio-fg-disabled' 'tio-fg-success'}}"
                  >
                    {{plan.forgiveness}}
                  </div>
                </div>
                <div>
                  <Text class="tio-fg-muted text-xs uppercase">Timeline</Text>
                  <div class="text-lg font-bold tio-fg-primary">
                    {{plan.timeline}}
                  </div>
                </div>
              </div>
            </div>
          </div>
        {{/each}}
      </div>

      {{! Understanding IDR }}
      <SectionHeader
        @icon={{PhShieldCheck}}
        @title="How IDR Works"
        @subtitle="Payments based on what you earn"
        @iconClass="tio-fg-info"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FeatureCard
          @icon={{PhCurrencyDollar}}
          @title="Income-Based Payments"
          @description="Your monthly payment is calculated as a percentage of your discretionary income, typically 10-20% depending on the plan. As your income changes, so does your payment."
          @color="teal"
        />
        <FeatureCard
          @icon={{PhClockCountdown}}
          @title="Forgiveness Timeline"
          @description="After 20-25 years of qualifying payments, any remaining balance is forgiven. If you work in public service, PSLF can forgive after just 10 years."
          @color="cyan"
        />
        <FeatureCard
          @icon={{PhCalendar}}
          @title="Annual Recertification"
          @description="You must recertify your income and family size each year to stay on your IDR plan. Missing recertification can increase your payment significantly."
          @color="amber"
        />
        <FeatureCard
          @icon={{PhLightbulb}}
          @title="SAVE Plan Advantage"
          @description="The SAVE plan caps payments at 5% of discretionary income for undergraduate loans and prevents unpaid interest from growing your balance."
          @color="purple"
        />
      </div>

      {{! Tools Section }}
      <SectionHeader
        @icon={{PhCalculator}}
        @title="IDR Tools"
        @subtitle="Resources to help you manage your plan"
        @iconClass="tio-fg-info"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <NavCard
          @layout="gradient"
          @title="Forgiveness Estimator"
          @subtitle="Calculate your potential forgiveness amount based on income and plan"
          @icon={{PhCalculator}}
          @route="authenticated.reimagined.plan.strategy"
        />
        <NavCard
          @layout="gradient"
          @title="Plan Comparison"
          @subtitle="Side-by-side comparison of all available IDR plans"
          @icon={{PhScales}}
          @route="authenticated.reimagined.plan.strategy"
        />
        <NavCard
          @layout="gradient"
          @title="Strategy Advisor"
          @subtitle="Get personalized recommendations for your situation"
          @icon={{PhRobot}}
          @route="authenticated.reimagined.advisor"
        />
      </div>

      {{! CTA }}
      <CtaBanner
        @heading="Not sure which plan is right?"
        @description="Our Strategy Finder analyzes your loans, income, and goals to recommend the best path."
        @intent="brand"
      >
        <:action>
          <LinkTo @route="authenticated.reimagined.plan.strategy">
            <Button @intent="primary">
              Find My Strategy
              <PhArrowRight class="ml-2 h-4 w-4" />
            </Button>
          </LinkTo>
        </:action>
      </CtaBanner>
    </VStack>
  </template>
}
